import PopupHeader from "@builder/components/app/Popup/PopupHeader";
import "./style.scss";
import { BaseLayer, BaseLayerTypes, Build, BuildNode } from "@common/build/types";
import React, { useEffect, useRef, useState } from "react";
import { getManagedBuilds } from "@builder/utils/mongoFunctions";
import Loading from "@builder/components/app/Loading";
import AdvSelectInput from "@builder/components/app/Settings/StoreData/AdvSelectInput";
import OliverText from "@builder/components/general/OliverText";
import { componentWrapperMap } from "@builder/components/app/OliverBuildFrame/map";
import { ActiveEntityType } from "@common/build/activeEntityType";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useTranslation } from "react-i18next";
// import { copyTemplateNode } from "@builder/store/slices/BuilderSlice/builderSlice";
// import { closePopup } from "@builder/store/slices/popupSlice";
import { buildSizes } from "@common/build/constants";
import { copyDesignSetLayer, installDesignSet } from "@builder/store/slices/BuilderSlice/builderSlice";
import { closePopup } from "@builder/store/slices/popupSlice";
import Button from "@builder/components/general/Button";
import { useOutsideClick } from "@builder/hooks/useOutsideClick";
// import TextInput from "@builder/components/general/TextInput";

const FullDesignSets = () => {
    const dispatch = useAppDispatch();
    const storeData = useAppSelector(state => state.storeData.data);
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [designSets, setDesignSets] = useState<Build[]>([]);
    const [currentDS, setCurrentDS] = useState<Build>();
    const [renderHeight, setRenderHeight] = useState<number>();
    const [scrollIndex, setScrollIndex] = useState<number>(0);
    const colRef = useRef<HTMLDivElement>(null);
    const HEADER_HEIGHT = 62;

    const getDynamicDataObject = (node: BuildNode) => {
        if (node.dynamicData) {
            const dType = node.dynamicData.split(".")[0];
            switch (dType) {
                case ActiveEntityType.COMPANY:
                    return storeData?.company;
                case ActiveEntityType.PRODUCT:
                    return storeData?.product;
                case ActiveEntityType.ORDER:
                    return storeData?.order;
                case ActiveEntityType.CUSTOMER:
                    return storeData?.customer;
            }
        }
    };

    const renderNodeNoFunction = (layer: BaseLayer, key: string): React.ReactNode => {
        if (layer.components[key]) {
            const node = JSON.parse(JSON.stringify(layer.components[key])) as BuildNode;

            if (node) {
                const dynamicDataObject = getDynamicDataObject(node);

                const Component = componentWrapperMap[node.name as keyof typeof componentWrapperMap];

                if (layer.root === key && currentDS?.size.key && colRef.current) {
                    const size = (currentDS?.size.key === "custom" && currentDS.size.frameSize
                        ? currentDS.size.frameSize
                        : buildSizes[currentDS.size.key]?.value) || { x: 1920, y: 1080 };

                    if (size.x > colRef.current.clientWidth) {
                        const scale = colRef.current.clientWidth / size.x;
                        node.attr = {
                            ...node.attr,
                            style: {
                                ...node.attr?.style,
                                transform: `scale(${scale})`,
                                width: `${size.x}px`,
                                height: size.y === "fit-content" ? "fit-content" : `${size.y}px`
                            }
                        };
                        if (size.y !== "fit-content") {
                            const newHeight = Math.round(size.y * scale);
                            if (renderHeight !== newHeight) {
                                setRenderHeight(Math.round(size.y * scale));
                            }
                        }
                    } else {
                        if (size.y !== "fit-content") {
                            const newHeight = Math.round(size.y * 1);
                            if (renderHeight !== newHeight) {
                                setRenderHeight(Math.round(size.y * 1));
                            }
                        }
                    }
                }

                return (
                    <Component
                        node={node}
                        key={key}
                        t={t}
                        builderProps={{
                            containerDisabled: true
                        }}
                        dataObject={dynamicDataObject}
                    >
                        {node.children?.map(node => renderNodeNoFunction(layer, node))}
                    </Component>
                );
            }
        }
    };

    useEffect(() => {
        const fetchDesignSets = async () => {
            setLoading(true);

            const fetchedDS = await getManagedBuilds();

            setDesignSets(fetchedDS);

            if (!currentDS && fetchedDS.length > 0) {
                setCurrentDS(fetchedDS[0]);
            }

            setLoading(false);
        };

        fetchDesignSets();
    }, []);

    const updateCurrentDS = (value: Build) => {
        setCurrentDS(value);
    };

    const handleScroll = () => {
        if (colRef.current && renderHeight) {
            const scrollTop = colRef.current.scrollTop + (renderHeight + HEADER_HEIGHT) / 2;
            setScrollIndex(Math.max(Math.floor(scrollTop / (renderHeight + HEADER_HEIGHT)), 0));
        }
    };

    const handleSidebarClick = (index: number) => {
        if (colRef.current && renderHeight) {
            colRef.current.scrollTo({ top: index * (renderHeight + HEADER_HEIGHT), behavior: "smooth" });
        }
    };

    const handleCopyLayer = (page: BaseLayer, type: BaseLayerTypes) => {
        dispatch(copyDesignSetLayer({ layer: page, type }));
        dispatch(closePopup());
    };

    // const getAction = (templateDiv: TemplateDiv): ActionBuilder | undefined => {
    //     const actionIds = templateDiv.node.actionsBuilder;
    //     if (actionIds?.[0] && currentTemplatePage?.actionBuilder?.[actionIds[0]]) {
    //         return currentTemplatePage.actionBuilder[actionIds[0]];
    //     }
    //     return undefined;
    // };

    // const copyTemplate = (templateDiv: TemplateDiv) => {
    //     const layer = currentTemplatePage?.[templateDiv.layer.type]?.[templateDiv.layer.index];
    //     const action = getAction(templateDiv);
    //     if (layer) {
    //         dispatch(copyTemplateNode({ layer, node: templateDiv.node, action }));
    //         dispatch(closePopup());
    //     }
    // };

    const [installConfirm, setInstallConfirm] = useState<boolean>(false);
    const confirmRef = useRef<HTMLDivElement>(null);

    useOutsideClick(confirmRef, () => setInstallConfirm(false));

    const handleInstallDS = () => {
        if (currentDS) {
            dispatch(installDesignSet(currentDS));
            dispatch(closePopup());
        }
    };

    return (
        <div className="full-ds-library">
            <PopupHeader src="/book-open.svg" title="Design Set Library">
                <div className="install-build">
                    {installConfirm ? (
                        <div className="confirm-wrapper" ref={confirmRef}>
                            <OliverText text="Current build will be lost. Proceed anyways?" size="s" weight="semi-bold" />
                            <Button buttonStyle="secondary" text="Cancel" className="confirm-button" onClick={() => setInstallConfirm(false)} />
                            <Button buttonStyle="primary" text="Confirm" className="confirm-button" onClick={handleInstallDS} />
                        </div>
                    ) : (
                        <Button
                            buttonStyle="primary"
                            className="install-build-button"
                            text="Install build"
                            disabled={!currentDS}
                            onClick={() => setInstallConfirm(true)}
                        />
                    )}
                </div>
            </PopupHeader>
            <div className="template-library-body">
                {loading || !currentDS ? (
                    <Loading />
                ) : (
                    <>
                        <div className="left-column">
                            <AdvSelectInput
                                value={currentDS}
                                options={designSets}
                                inputValue=""
                                label="Design sets"
                                hasMore={false}
                                findLabel={option => option?.name || ""}
                                clickCallback={updateCurrentDS}
                                scrollCallback={() => {}}
                                searchCallback={() => {}}
                                disabled={loading}
                            />

                            {currentDS.pages.length > 0 && <OliverText text="Pages" size="xs" weight="medium-heavy" />}
                            {currentDS.pages.map((page, index) => (
                                <button className={scrollIndex === index ? "current" : ""} key={page.id} onClick={() => handleSidebarClick(index)}>
                                    {page.name || `Page #${index + 1}`}
                                </button>
                            ))}
                            {currentDS.popups.length > 0 && <OliverText text="Popups" size="xs" weight="medium-heavy" />}
                            {currentDS.popups.map((popup, index) => (
                                <button className={scrollIndex === index ? "current" : ""} key={popup.id} onClick={() => handleSidebarClick(index)}>
                                    {popup.name || `Popup #${index + 1}`}
                                </button>
                            ))}
                        </div>
                        <div className="right-column" ref={colRef} onScroll={handleScroll}>
                            {currentDS.pages.map((page, index) => {
                                return (
                                    <div className="template-div" key={page.id}>
                                        <div className="t-d-header">
                                            <OliverText text={page.name || `Page #${index + 1}`} size="l" weight="semi-bold" />
                                            <button onClick={() => handleCopyLayer(page, BaseLayerTypes.PAGES)}>
                                                <img src="/copy-icon.svg" alt="" />
                                                Copy page
                                            </button>
                                        </div>
                                        <div
                                            className="t-d-render"
                                            style={{ fontFamily: "poppins", ...(renderHeight ? { height: `${renderHeight}px` } : {}) }}
                                        >
                                            {page.components[page.root] && renderNodeNoFunction(page, page.root)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default FullDesignSets;
